import type React from 'react';
import { Fragment } from 'react';
import { Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { buildIcs234MatrixTableRows } from './buildIcs234MatrixTableRows';
import { ICS234_BOX_LABELS } from './constants';
import {
  getIcs234MatrixItemDraftName,
  Ics234MatrixInlineNameField,
  Ics234MatrixItemEditControls,
  type Ics234WorkAnalysisMatrixProps,
} from './Ics234MatrixShared';
import {
  MATRIX_KIND_BADGE,
  MATRIX_META_BADGE,
  MATRIX_OUTLINE_BTN,
  MATRIX_TRASH_BTN,
} from './matrixStyles';
import type { Ics234MatrixItemRef } from './types';
import { ics234MatrixItemKey, ics234MatrixItemRefsMatch } from './types';

const TABLE_CELL_CLASS =
  'tw:min-w-[14rem] tw:max-w-[24rem] tw:border-b tw:border-neutral-700 tw:px-3 tw:py-3 tw:align-top tw:whitespace-pre-wrap tw:break-words tw:text-white';

const TABLE_NAME_FIELD_CLASS =
  'tw:block tw:w-full tw:whitespace-pre-wrap tw:break-words tw:text-white';

const TABLE_MIN_WIDTH = '42rem';

type MatrixTableRow = ReturnType<typeof buildIcs234MatrixTableRows>[number];

export const Ics234WorkAnalysisMatrixTable: React.FC<
  Ics234WorkAnalysisMatrixProps
> = ({
  objectives,
  canEdit,
  isSaving,
  editingItem,
  onStartItemEdit,
  onCancelItemEdit,
  onPatchItemDraft,
  onSaveItem,
  onAddStrategy,
  onAddTactic,
  onDeleteObjective,
  onDeleteStrategy,
  onDeleteTactic,
}) => {
  const canMutate = canEdit;
  const rows = buildIcs234MatrixTableRows(objectives);

  const isEditingRef = (ref: Ics234MatrixItemRef) =>
    !!editingItem && ics234MatrixItemRefsMatch(editingItem.ref, ref);

  const renderEmptyCell = () => (
    <span className="tw:text-muted-foreground">—</span>
  );

  const renderObjectiveCell = (row: MatrixTableRow) => {
    const { objective, objectiveIndex } = row;
    const ref: Ics234MatrixItemRef = {
      kind: 'objective',
      objectiveId: objective.id,
    };
    const editing = isEditingRef(ref);
    const name = getIcs234MatrixItemDraftName(ref, objective.name, editingItem);
    const placeholder = `Objective ${objectiveIndex + 1}`;
    const hasChildren = objective.strategies.length > 0;

    return (
      <td rowSpan={row.objectiveRowSpan} className={TABLE_CELL_CLASS}>
        <div className="tw:flex tw:items-start tw:gap-2">
          <div className="tw:min-w-0 tw:flex-1 tw:space-y-2">
            <Ics234MatrixInlineNameField
              name={name}
              editing={editing}
              placeholder={placeholder}
              className={TABLE_NAME_FIELD_CLASS}
              onChange={(next) =>
                onPatchItemDraft({ kind: 'objective', name: next })
              }
            />
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
              <Badge variant="outline" className={MATRIX_KIND_BADGE}>
                Objective
              </Badge>
              <Badge variant="secondary" className={MATRIX_META_BADGE}>
                Operational
              </Badge>
            </div>
          </div>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            <Ics234MatrixItemEditControls
              itemRef={ref}
              itemLabel="objective"
              canMutate={canMutate}
              isSaving={isSaving}
              editingItem={editingItem}
              onStartItemEdit={onStartItemEdit}
              onCancelItemEdit={onCancelItemEdit}
              onSaveItem={onSaveItem}
              compact
            />
            {canMutate && !editing ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Delete objective"
                className={MATRIX_TRASH_BTN}
                disabled={hasChildren}
                onClick={() => {
                  onDeleteObjective(objective.id);
                  if (
                    editingItem &&
                    (editingItem.ref.objectiveId === objective.id ||
                      ics234MatrixItemKey(editingItem.ref).startsWith(
                        `str-${objective.id}-`
                      ) ||
                      ics234MatrixItemKey(editingItem.ref).startsWith(
                        `tac-${objective.id}-`
                      ))
                  ) {
                    onCancelItemEdit();
                  }
                }}
              >
                <Trash2 className="tw:h-4 tw:w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </td>
    );
  };

  const renderStrategyCell = (row: MatrixTableRow) => {
    if (!row.strategy || row.strategyIndex === null) {
      return (
        <td className={TABLE_CELL_CLASS}>
          {canMutate ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                'tw:h-7 tw:shrink-0 tw:text-xs',
                MATRIX_OUTLINE_BTN
              )}
              onClick={() => onAddStrategy(row.objective.id)}
            >
              + Strategy
            </Button>
          ) : (
            renderEmptyCell()
          )}
        </td>
      );
    }

    const { objective, strategy, strategyIndex } = row;
    const ref: Ics234MatrixItemRef = {
      kind: 'strategy',
      objectiveId: objective.id,
      strategyId: strategy.id,
    };
    const editing = isEditingRef(ref);
    const name = getIcs234MatrixItemDraftName(ref, strategy.name, editingItem);
    const placeholder = `Strategy ${strategyIndex + 1}`;
    const hasChildren = strategy.tactics.length > 0;

    return (
      <td rowSpan={row.strategyRowSpan ?? 1} className={TABLE_CELL_CLASS}>
        <div className="tw:flex tw:items-start tw:gap-2">
          <div className="tw:min-w-0 tw:flex-1 tw:space-y-2">
            <Ics234MatrixInlineNameField
              name={name}
              editing={editing}
              placeholder={placeholder}
              className={TABLE_NAME_FIELD_CLASS}
              onChange={(next) =>
                onPatchItemDraft({ kind: 'strategy', name: next })
              }
            />
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
              <Badge variant="outline" className={MATRIX_KIND_BADGE}>
                Strategy
              </Badge>
              {canMutate && !editing && strategyIndex === 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    'tw:h-7 tw:shrink-0 tw:text-xs',
                    MATRIX_OUTLINE_BTN
                  )}
                  onClick={() => onAddStrategy(objective.id)}
                >
                  + Strategy
                </Button>
              ) : null}
            </div>
          </div>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            <Ics234MatrixItemEditControls
              itemRef={ref}
              itemLabel="strategy"
              canMutate={canMutate}
              isSaving={isSaving}
              editingItem={editingItem}
              onStartItemEdit={onStartItemEdit}
              onCancelItemEdit={onCancelItemEdit}
              onSaveItem={onSaveItem}
              compact
            />
            {canMutate && !editing ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Delete strategy"
                className={MATRIX_TRASH_BTN}
                disabled={hasChildren}
                onClick={() => {
                  onDeleteStrategy(objective.id, strategy.id);
                  if (
                    editingItem &&
                    ics234MatrixItemKey(editingItem.ref).startsWith(
                      `str-${objective.id}-${strategy.id}`
                    )
                  ) {
                    onCancelItemEdit();
                  }
                }}
              >
                <Trash2 className="tw:h-4 tw:w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </td>
    );
  };

  const renderTacticCell = (row: MatrixTableRow) => {
    if (!row.strategy || !row.tactic || row.tacticIndex === null) {
      return (
        <td className={TABLE_CELL_CLASS}>
          {canMutate && row.strategy ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                'tw:h-7 tw:shrink-0 tw:text-xs',
                MATRIX_OUTLINE_BTN
              )}
              onClick={() => onAddTactic(row.objective.id, row.strategy!.id)}
            >
              + Tactic
            </Button>
          ) : (
            renderEmptyCell()
          )}
        </td>
      );
    }

    const { objective, strategy, tactic, tacticIndex } = row;
    const ref: Ics234MatrixItemRef = {
      kind: 'tactic',
      objectiveId: objective.id,
      strategyId: strategy.id,
      tacticId: tactic.id,
    };
    const editing = isEditingRef(ref);
    const name = getIcs234MatrixItemDraftName(ref, tactic.name, editingItem);
    const placeholder = `Tactic ${tacticIndex + 1}`;

    return (
      <td className={TABLE_CELL_CLASS}>
        <div className="tw:flex tw:items-start tw:gap-2">
          <div className="tw:min-w-0 tw:flex-1 tw:space-y-2">
            <Ics234MatrixInlineNameField
              name={name}
              editing={editing}
              placeholder={placeholder}
              className={TABLE_NAME_FIELD_CLASS}
              onChange={(next) =>
                onPatchItemDraft({ kind: 'tactic', name: next })
              }
            />
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
              <Badge variant="outline" className={MATRIX_KIND_BADGE}>
                Tactic
              </Badge>
              {canMutate && !editing && tacticIndex === 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    'tw:h-7 tw:shrink-0 tw:text-xs',
                    MATRIX_OUTLINE_BTN
                  )}
                  onClick={() => onAddTactic(objective.id, strategy.id)}
                >
                  + Tactic
                </Button>
              ) : null}
            </div>
          </div>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            <Ics234MatrixItemEditControls
              itemRef={ref}
              itemLabel="tactic"
              canMutate={canMutate}
              isSaving={isSaving}
              editingItem={editingItem}
              onStartItemEdit={onStartItemEdit}
              onCancelItemEdit={onCancelItemEdit}
              onSaveItem={onSaveItem}
              compact
            />
            {canMutate && !editing ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Delete tactic"
                className={MATRIX_TRASH_BTN}
                onClick={() =>
                  onDeleteTactic(objective.id, strategy.id, tactic.id)
                }
              >
                <Trash2 className="tw:h-4 tw:w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </td>
    );
  };

  return (
    <div className="tw:min-w-0 tw:w-full tw:max-w-full">
      {objectives.length === 0 ? (
        <Item
          variant="outline"
          className="tw:border-neutral-600 tw:bg-neutral-950"
        >
          <ItemContent>
            <ItemTitle className="tw:text-white">
              No objectives recorded
            </ItemTitle>
            <ItemDescription className="tw:text-neutral-400">
              Add objectives to build the work analysis matrix.
            </ItemDescription>
          </ItemContent>
        </Item>
      ) : (
        <div className="tw:min-w-0 tw:w-full tw:max-w-full tw:overflow-hidden tw:rounded-md tw:border tw:border-neutral-700">
          <div
            className="tw:w-full tw:overflow-x-auto tw:overscroll-x-contain tw:touch-pan-x tw:[scrollbar-gutter:stable]"
            tabIndex={0}
            aria-label="Work analysis matrix table — scroll horizontally to view additional columns"
          >
            <table
              className="tw:w-full tw:border-separate tw:border-spacing-0 tw:text-xs tw:text-white"
              style={{ minWidth: TABLE_MIN_WIDTH }}
            >
              <thead>
                <tr className="tw:bg-neutral-900 tw:text-left tw:text-neutral-300">
                  <th
                    className={cn(
                      TABLE_CELL_CLASS,
                      'tw:border-b tw:border-neutral-700'
                    )}
                  >
                    <div className="tw:font-semibold tw:text-white">
                      {ICS234_BOX_LABELS.objectives}
                    </div>
                    <div className="tw:text-[10px] tw:font-normal tw:text-neutral-300">
                      {ICS234_BOX_LABELS.objectivesSub}
                    </div>
                  </th>
                  <th
                    className={cn(
                      TABLE_CELL_CLASS,
                      'tw:border-b tw:border-neutral-700'
                    )}
                  >
                    <div className="tw:font-semibold tw:text-white">
                      {ICS234_BOX_LABELS.strategies}
                    </div>
                    <div className="tw:text-[10px] tw:font-normal tw:text-neutral-300">
                      {ICS234_BOX_LABELS.strategiesSub}
                    </div>
                  </th>
                  <th
                    className={cn(
                      TABLE_CELL_CLASS,
                      'tw:border-b tw:border-neutral-700'
                    )}
                  >
                    <div className="tw:font-semibold tw:text-white">
                      {ICS234_BOX_LABELS.tactics}
                    </div>
                    <div className="tw:text-[10px] tw:font-normal tw:text-neutral-300">
                      {ICS234_BOX_LABELS.tacticsSub}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const groupStart = row.showObjective && rowIndex > 0;
                  return (
                    <Fragment key={row.rowKey}>
                      {groupStart ? (
                        <tr aria-hidden="true">
                          <td colSpan={3} className="tw:border-0 tw:p-0">
                            <div className="tw:h-0.5 tw:w-full tw:bg-border" />
                          </td>
                        </tr>
                      ) : null}
                      <tr className="tw:align-top">
                        {row.showObjective ? renderObjectiveCell(row) : null}
                        {row.showStrategy ||
                        (!row.strategy && row.showObjective)
                          ? renderStrategyCell(row)
                          : null}
                        {renderTacticCell(row)}
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
